import React from 'react';
import { useGetOne, useRedirect, useResourceContext } from 'react-admin';
import { Breadcrumbs, Link, Typography, Box, IconButton } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams } from 'react-router-dom';

interface ResourceBreadcrumbsProps {
  // Optional: Override the resource name
  resource?: string;
  // Optional: Custom display names
  customNames?: {
    singular?: string;
    plural?: string;
  };
  // Optional: Parent resource info for nested resources
  parentResource?: {
    name: string;
    id: string | number;
    displayName?: string;
  };
}

const ResourceBreadcrumbs: React.FC<ResourceBreadcrumbsProps> = ({
  resource: resourceProp,
  customNames,
  parentResource,
}) => {
  const redirect = useRedirect();
  const resourceContext = useResourceContext();
  const { id } = useParams();

  // Use prop resource or context resource
  const resource = resourceProp || resourceContext;

  // Fetch the current item data
  const { data: itemData, isLoading } = useGetOne(
    resource,
    { id },
    { enabled: !!id && !!resource }
  );

  if (!resource || !id) {
    return null;
  }

  // Resource display name mappings
  const resourceMap: Record<string, { singular: string; plural: string }> = {
    equipment: { singular: 'Vehicle', plural: 'Vehicles' },
    users: { singular: 'User', plural: 'Users' },
    drivers: { singular: 'Driver', plural: 'Drivers' },
    customers: { singular: 'Customer', plural: 'Customers' },
    leads: { singular: 'Lead', plural: 'Leads' },
    opportunities: { singular: 'Opportunity', plural: 'Opportunities' },
    tasks: { singular: 'Task', plural: 'Tasks' },
    'equipment-maintenance': { singular: 'Maintenance', plural: 'Maintenance Records' },
    'equipment-fuel': { singular: 'Fuel Record', plural: 'Fuel Records' },
    'equipment-incidents': { singular: 'Incident', plural: 'Incidents' },
    'equipment-inspections': { singular: 'Inspection', plural: 'Inspections' },
    'equipment-assignments': { singular: 'Assignment', plural: 'Assignments' },
    brands: { singular: 'Brand', plural: 'Brands' },
    regions: { singular: 'Region', plural: 'Regions' },
    enterprises: { singular: 'Enterprise', plural: 'Enterprises' },
  };

  // Get resource names
  const resourceNames = customNames ||
    resourceMap[resource] || {
      singular: resource.charAt(0).toUpperCase() + resource.slice(1),
      plural: resource.charAt(0).toUpperCase() + resource.slice(1) + 's',
    };

  // Handle navigation
  const handleNavigation = (path: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    redirect(path);
  };

  // Handle browser back navigation
  const handleBackNavigation = () => {
    window.history.back();
  };

  if (isLoading) {
    return null;
  }

  // Determine the item display name
  const getItemDisplayName = () => {
    if (!itemData) {
      return `${resourceNames.singular} ${id}`;
    }

    // Common name fields to check
    const nameFields = ['name', 'title', 'label', 'displayName'];
    for (const field of nameFields) {
      if (itemData[field]) {
        return itemData[field];
      }
    }

    // For users, try email
    if (resource === 'users' && itemData.email) {
      return itemData.email;
    }

    // For drivers, try to get user name
    if (resource === 'drivers' && itemData.user?.name) {
      return itemData.user.name;
    }

    return `${resourceNames.singular} ${id}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '16px 20px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        '&:hover': {
          backgroundColor: '#f1f5f9',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        transition: 'all 0.2s ease',
      }}
    >
      {/* Back Navigation Button */}
      <IconButton
        onClick={handleBackNavigation}
        sx={{
          width: 36,
          height: 36,
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          '&:hover': {
            backgroundColor: '#1976d2',
            color: 'white',
            borderColor: '#1976d2',
          },
          transition: 'all 0.2s ease',
        }}
        title="Go back"
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>

      {/* Enhanced Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" sx={{ color: '#64748b' }} />} 
        aria-label="breadcrumb"
        sx={{ flex: 1 }}
      >
        {/* Parent resource link if provided */}
        {parentResource && (
          <Link
            underline="hover"
            color="inherit"
            href={`/${parentResource.name}/${parentResource.id}/show`}
            onClick={handleNavigation(`/${parentResource.name}/${parentResource.id}/show`)}
            sx={{
              fontWeight: 'medium',
              fontSize: '0.95rem',
              color: '#475569',
              '&:hover': { 
                color: '#1976d2',
                textDecoration: 'underline',
              },
              transition: 'color 0.2s ease',
            }}
          >
            {parentResource.displayName || parentResource.name}
          </Link>
        )}

        {/* Link to the list view */}
        <Link
          underline="hover"
          color="inherit"
          href={`/${resource}`}
          onClick={handleNavigation(`/${resource}`)}
          sx={{
            fontWeight: 'medium',
            fontSize: '0.95rem',
            color: '#475569',
            '&:hover': { 
              color: '#1976d2',
              textDecoration: 'underline',
            },
            transition: 'color 0.2s ease',
          }}
        >
          {resourceNames.plural}
        </Link>

        {/* Current item name */}
        <Typography 
          color="text.primary" 
          sx={{ 
            fontWeight: 600,
            fontSize: '0.95rem',
            color: '#1e293b',
          }}
        >
          {getItemDisplayName()}
        </Typography>
      </Breadcrumbs>
    </Box>
  );
};

export default ResourceBreadcrumbs;
